// src/features/media/components/VideoGeneralInfoForm.tsx

import { Stack, TextInput, Textarea, Switch, Divider, Loader } from '@mantine/core';
import { MarkdownField } from '@/shared/components/MarkdownField/MarkdownField';
import { DateTimeField } from '@/shared/components/DateTimeField';
import { useController, type Control, type UseFormWatch } from 'react-hook-form';
import { DateTime } from 'luxon';
import { VIDEO_CONSTRAINTS } from '@/lib/constants/media';
import { VideoAvailabilityFields } from './VideoAvailabilityFields';
import { TagInput } from '@/shared/components/TagInput';
import type { VideoFormData } from '../schemas/VideoForm.schema';
import type { VideoViewData } from '@/lib/types/Media';

interface VideoGeneralInfoFormProps {
  control: Control<VideoFormData>;
  watch: UseFormWatch<VideoFormData>;
  video: VideoViewData;
  allTags: string[];
  /** Uniqueness conflict message for the Reference ID field (from useRefIdConflict). */
  refIdConflictError?: string | null;
  refIdChecking?: boolean;
}

/**
 * Convert Unix timestamp (seconds, UTC) to DateTimePicker string value.
 * Luxon fromSeconds() converts to the system's local timezone.
 * Format: 'YYYY-MM-DD HH:mm:ss' (Mantine 8 internal value format)
 */
function timestampToDateStr(ts: number | null | undefined): string | null {
  if (typeof ts !== 'number' || !isFinite(ts)) return null;
  return DateTime.fromSeconds(ts).toFormat('yyyy-MM-dd HH:mm:ss');
}

/**
 * Convert DateTimePicker string value back to Unix timestamp (seconds, UTC).
 * Luxon fromFormat() parses in local timezone, then toUTC().toSeconds()
 * converts to UTC epoch seconds for the API.
 */
function dateStrToTimestamp(dateStr: string): number {
  const dt = DateTime.fromFormat(dateStr, 'yyyy-MM-dd HH:mm:ss');
  return Math.floor(dt.toUTC().toSeconds());
}

export function VideoGeneralInfoForm({ control, watch, video, allTags, refIdConflictError, refIdChecking }: VideoGeneralInfoFormProps) {
  // --- Field controllers ---

  const { field: titleField, fieldState: titleState } = useController({ control, name: 'title' });
  const titleLength = watch('title')?.length ?? 0;

  const { field: descShortField, fieldState: descShortState } = useController({ control, name: 'descShort' });
  const descShortLength = watch('descShort')?.length ?? 0;

  const { field: tagsField, fieldState: tagsState } = useController({ control, name: 'tags' });

  const { field: stateField } = useController({ control, name: 'mediaStateType' });
  const isActive = stateField.value === 'active';

  const { field: publishedField } = useController({ control, name: 'published' });

  const { field: relatedLinkUrlField, fieldState: relatedLinkUrlState } = useController({ control, name: 'relatedLinkUrl' });
  const { field: relatedLinkTextField } = useController({ control, name: 'relatedLinkText' });
  const { field: refIdField, fieldState: refIdState } = useController({ control, name: 'refId' });

  const { field: descLongField, fieldState: descLongState } = useController({ control, name: 'descLong' });
  const descLongLength = watch('descLong')?.length ?? 0;

  const { field: notesField, fieldState: notesState } = useController({ control, name: 'notes' });
  const notesLength = watch('notes')?.length ?? 0;

  return (
    <Stack gap="md">
      {/* Title */}
      <TextInput
        label="Title"
        value={titleField.value}
        onChange={titleField.onChange}
        onBlur={titleField.onBlur}
        error={titleState.error?.message}
        description={`${titleLength}/${VIDEO_CONSTRAINTS.TITLE_MAX}`}
        maxLength={VIDEO_CONSTRAINTS.TITLE_MAX}
      />

      {/* Short Description */}
      <Textarea
        label="Short Description"
        value={descShortField.value}
        onChange={descShortField.onChange}
        onBlur={descShortField.onBlur}
        error={descShortState.error?.message}
        description={`${descShortLength}/${VIDEO_CONSTRAINTS.DESC_SHORT_MAX}`}
        maxLength={VIDEO_CONSTRAINTS.DESC_SHORT_MAX}
        rows={3}
      />

      {/* Tags */}
      <TagInput
        value={tagsField.value}
        onChange={tagsField.onChange}
        onBlur={tagsField.onBlur}
        allTags={allTags}
        error={tagsState.error?.message}
      />

      <Divider />

      {/* Status & Scheduling */}
      <Switch
        label={isActive ? 'Active — video is publicly available' : 'Inactive — video is hidden from viewers'}
        checked={isActive}
        onChange={(event) => {
          stateField.onChange(event.currentTarget.checked ? 'active' : 'inactive');
        }}
        color={isActive ? 'green' : 'gray'}
        size="md"
      />

      <DateTimeField
        label="Published"
        value={timestampToDateStr(publishedField.value)}
        onChange={(val) => {
          if (val) {
            publishedField.onChange(dateStrToTimestamp(val));
          }
          // Ignore null/clear — published must always have a value
        }}
        valueFormat="MMM D, YYYY h:mm A"
        timePickerProps={{ format: '12h' }}
        clearable={false}
      />

      <VideoAvailabilityFields control={control} />

      <Divider />

      {/* Links & Reference */}
      <TextInput
        label="Related Link URL"
        value={relatedLinkUrlField.value}
        onChange={relatedLinkUrlField.onChange}
        onBlur={relatedLinkUrlField.onBlur}
        error={relatedLinkUrlState.error?.message}
        placeholder="https://example.com"
        maxLength={VIDEO_CONSTRAINTS.RELATED_LINK_URL_MAX}
      />

      <TextInput
        label="Related Link Text"
        value={relatedLinkTextField.value}
        onChange={relatedLinkTextField.onChange}
        onBlur={relatedLinkTextField.onBlur}
        placeholder="Link display text"
        maxLength={VIDEO_CONSTRAINTS.RELATED_LINK_TEXT_MAX}
      />

      <TextInput
        label="Reference ID"
        value={refIdField.value}
        onChange={refIdField.onChange}
        onBlur={refIdField.onBlur}
        error={refIdState.error?.message ?? refIdConflictError ?? undefined}
        description="Letters, numbers, hyphens, underscores only"
        maxLength={VIDEO_CONSTRAINTS.REF_ID_MAX}
        rightSection={refIdChecking ? <Loader size="xs" /> : undefined}
      />

      <Divider />

      {/* Extended Content */}
      <MarkdownField
        label="Long Description"
        description={`${descLongLength}/${VIDEO_CONSTRAINTS.DESC_LONG_MAX}`}
        value={descLongField.value}
        onChange={descLongField.onChange}
        onBlur={descLongField.onBlur}
        error={descLongState.error?.message}
        maxLength={VIDEO_CONSTRAINTS.DESC_LONG_MAX}
        rows={6}
      />

      <Textarea
        label="Internal Notes"
        value={notesField.value}
        onChange={notesField.onChange}
        onBlur={notesField.onBlur}
        error={notesState.error?.message}
        description={`${notesLength}/${VIDEO_CONSTRAINTS.NOTES_MAX}`}
        maxLength={VIDEO_CONSTRAINTS.NOTES_MAX}
        rows={4}
      />
    </Stack>
  );
}
